import { useCallback, useState } from 'react';

import { useNotification } from 'hooks';

import { EVAULT_API, useEvaultApi } from './use-evault-api';

interface IVaultCopy {
	name: string;
	mimeType: string;
	base64: string;
}

/** Long enough for the new tab to have fetched the blob; short enough not to pin
 *  a multi-megabyte PDF in memory for the rest of the session. */
const REVOKE_AFTER_MS = 60_000;

/**
 * Open the watermarked ("COPY") copy of a vaulted document in a new tab.
 *
 * Used by the two places a previous owner meets a document they no longer
 * control: the Transfers row's document list, and the drawer footer of a
 * retained copy. The server renders the watermark per request — `retainedBy` is
 * an entitlement, not a stored file — so there is no URL to link to and the
 * bytes arrive base64 in a JSON envelope.
 *
 * THE TAB IS OPENED BEFORE THE REQUEST, not after it. A `window.open` that
 * happens in a promise continuation has lost the user gesture that authorises
 * it, and every popup blocker swallows it silently — the click would read as a
 * dead button. Opened synchronously it is the blocker's problem, not ours; and
 * if the blocker refuses even that, the same-tab navigation at the end still
 * gets the document on screen.
 */
export const useViewCopy = () => {
	const api = useEvaultApi();
	const { errorNotification } = useNotification();
	/* Also the disabled flag for the button that started it — one document at a
	   time, so a double-click cannot open two tabs and bill two audit entries. */
	const [busyId, setBusyId] = useState<string | null>(null);

	const viewCopy = useCallback(
		async (docId: string, name: string): Promise<void> => {
			if (!docId || busyId) return;

			/* THE VAULT SESSION MAY NOT EXIST YET. `useVaultToken` mints on mount,
			   so for the first moment after the page renders the bearer is an empty
			   string — a request sent then 401s, and the user sees a failure that
			   fixes itself if they simply click again. Refuse plainly instead, and
			   never open a tab we are only going to close. */
			if (!api.vaultToken) {
				errorNotification(
					api.vaultTokenError ||
						'Starting your eVault session — try again in a moment.'
				);
				return;
			}

			setBusyId(docId);

			const tab = window.open('', '_blank');

			try {
				const result = await api.get<IVaultCopy>(
					`${EVAULT_API.VAULT_COPIES}/${docId}`
				);

				if (result.kind !== 'ok' || !result.data?.base64) {
					tab?.close();
					errorNotification(
						result.kind === 'ok'
							? `Could not open a copy of ${name}.`
							: result.message
					);
					return;
				}

				const binary = window.atob(result.data.base64);
				const bytes = new Uint8Array(binary.length);
				for (let i = 0; i < binary.length; i += 1) {
					bytes[i] = binary.charCodeAt(i);
				}
				const url = URL.createObjectURL(
					new Blob([bytes], {
						type: result.data.mimeType || 'application/pdf',
					})
				);

				if (tab) {
					tab.location.href = url;
				} else {
					window.location.assign(url);
				}
				window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
			} catch {
				tab?.close();
				errorNotification(`Could not open a copy of ${name}.`);
			} finally {
				setBusyId(null);
			}
		},
		[api, busyId, errorNotification]
	);

	/* `ready` is what the buttons disable on, alongside `busyId`: a control that
	   cannot work yet should look like it, not fail on click. */
	return { viewCopy, busyId, ready: !!api.vaultToken };
};
